import type { ReactElement } from "react"
import React from "react"
import _t from "@core/i18n"
import SliderBarAtm from "@onestore/hel/dist/components/atoms/SliderBarAtm"
import TextAtm from "@onestore/hel/dist/components/atoms/TextAtm"
import InputMol from "@onestore/hel/dist/components/molecules/InputMol"
import SelectMol from "@onestore/hel/dist/components/molecules/SelectMol"
import type { SelectOption } from "@onestore/hel/dist/components/molecules/SelectMol/SelectMol.types"
import FlexContainerOrg from "@onestore/hel/dist/components/organisms/FlexContainerOrg"
import PushOrg from "@onestore/hel/dist/components/organisms/PushOrg"
import WidthOrg from "@onestore/hel/dist/components/organisms/WidthOrg"
import { sendCostCalculatorClickConfig } from "~/lib/analytics"
import isEmpty from "~/lib/isEmpty"
import type { HookResult } from "../useCostCalculator"

export type ConfigurationBoxProps = {
  connectedControlsCalculatorTitle: string
  connectedControlsSelect: SelectOption[]
  optionsCalculatorTitle: string
  parametersSelect: SelectOption[]
  maxUsersNumber: number
  usersNumber: HookResult["usersNumber"]
  connectedProductSelectOnChange: HookResult["connectedProductSelectOnChange"]
  parametersSelectOnChange: HookResult["parametersSelectOnChange"]
  inputRangeOnChange: HookResult["inputRangeOnChange"]
  inputNumberOnChange: HookResult["inputNumberOnChange"]
}

export default function ConfigurationBox({
  connectedControlsCalculatorTitle,
  connectedControlsSelect,
  optionsCalculatorTitle,
  parametersSelect,
  maxUsersNumber,
  usersNumber,
  connectedProductSelectOnChange,
  parametersSelectOnChange,
  inputRangeOnChange,
  inputNumberOnChange,
}: ConfigurationBoxProps): ReactElement<ConfigurationBoxProps> | null {
  return (
    <>
      <TextAtm typography="large3" pushSpace={3}>
        {_t("marketplace.costCalculatorTitle")}
      </TextAtm>

      {!isEmpty(connectedControlsSelect) ? (
        <PushOrg
          bottomSpace={{
            large: 3,
            medium: 3,
            small: 1.5,
          }}
        >
          <TextAtm typography="medium3_s" pushSpace={1}>
            {connectedControlsCalculatorTitle}
          </TextAtm>

          <SelectMol
            options={connectedControlsSelect}
            onChange={connectedProductSelectOnChange}
            onClick={() => {
              sendCostCalculatorClickConfig(connectedControlsCalculatorTitle)
            }}
          />
        </PushOrg>
      ) : null}

      {!isEmpty(parametersSelect) ? (
        <PushOrg
          bottomSpace={{
            large: 3,
            medium: 3,
            small: 1.5,
          }}
        >
          <TextAtm typography="medium3_s" pushSpace={1}>
            {optionsCalculatorTitle}
          </TextAtm>

          <SelectMol
            options={parametersSelect}
            onChange={parametersSelectOnChange}
            onClick={() => {
              sendCostCalculatorClickConfig(optionsCalculatorTitle)
            }}
          />
        </PushOrg>
      ) : null}

      <PushOrg bottomSpace={1}>
        <TextAtm typography="medium3_s" pushSpace={1}>
          {_t("marketplace.usersCount")}
        </TextAtm>

        <SliderBarAtm
          value={usersNumber}
          minValue={1}
          maxValue={maxUsersNumber}
          onChange={inputRangeOnChange}
          onBeforeChange={() => {
            sendCostCalculatorClickConfig(_t("marketplace.usersCount"))
          }}
        />
      </PushOrg>

      <FlexContainerOrg justifyContent="center" alignItems="center">
        <WidthOrg widthSpace={8}>
          <InputMol
            type="number"
            size="small"
            onChange={(event) => inputNumberOnChange(event, maxUsersNumber)}
            value={`${usersNumber}`}
            onClick={() => {
              sendCostCalculatorClickConfig(_t("marketplace.usersCount"))
            }}
            hasHiddenNumberControls
            hasCenteredValue
          />
        </WidthOrg>

        <PushOrg leftSpace={0.5}>
          <TextAtm
            typography="small2"
            emphasis="medium"
          >{`/ ${maxUsersNumber}`}</TextAtm>
        </PushOrg>
      </FlexContainerOrg>
    </>
  )
}
